import {Location} from 'react-router';
import pick from 'lodash/pick';

import {getQueryStringParam} from 'common/contexts/react/react-router';
import {getBehaviorShowClauses, isBehaviorShowClause} from 'common/report/insights/models/utils';
import {NthTimeOperator} from 'common/widgets/property-filter-menu/models/types';

import {REPORT_LIMITS, ReportName} from '../constants';
import {QueryManager} from '../report-manager/query';
import {BaseQueryErrorType, ParamsAndResults} from '../types';
import {FeatureRolloutName, FeatureRollouts} from '../util/feature-sampling';
import {CurrentProject} from '../model-resources/projects';
import {InsightsBookmarkParams, InsightsQueryError, InsightsQueryResults, InsightsResourceType} from './util/types';
import {addQueryLimitInfo, isCohortsQuery} from './util';

export class InsightsQueryManager extends QueryManager<
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsQueryError
> {
  public reportName = ReportName.Insights;
  public endpoint = `insights`;

  public sanitizeBookmarkParams(bookmarkParams: InsightsBookmarkParams): InsightsBookmarkParams {
    // If a show clause has the "First Time Filter", then remove any redundant segmentMethod values
    const sanitizedShowClauses = bookmarkParams.sections.show.map((clause) =>
      isBehaviorShowClause(clause) &&
      clause.behavior.filters?.some((filter) => filter.filterOperator === NthTimeOperator.FirstTime) &&
      clause.measurement.segmentMethod
        ? {
            ...clause,
            measurement: {
              ...clause.measurement,
              segmentMethod: null,
            },
          }
        : clause,
    );

    const sanitizedBookmarkParams = {
      ...bookmarkParams,
      sections: {
        ...bookmarkParams.sections,
        show: sanitizedShowClauses,
      },
    };

    return sanitizedBookmarkParams;
  }

  public getExtraCsvPostParams(bookmarkParams: InsightsBookmarkParams, currentProject: CurrentProject) {
    if (isCohortsQuery(bookmarkParams)) {
      return {};
    }

    return {
      queryLimits: {limit: REPORT_LIMITS[ReportName.Insights].csvExport},
      is_new_count_menu: FeatureRollouts[FeatureRolloutName.InsightsNewCountMenu].isEnabled(currentProject),
    };
  }

  public getExtraPostParams({
    bookmarkParams,
    currentProject,
    location,
  }: {
    bookmarkParams: InsightsBookmarkParams;
    currentProject: CurrentProject;
    location: Location;
  }) {
    if (isCohortsQuery(bookmarkParams)) {
      return {
        is_new_count_menu: FeatureRollouts[FeatureRolloutName.InsightsNewCountMenu].isEnabled(currentProject),
      };
    }

    const urlLimit = getQueryStringParam(location, `limit`);
    const limit = urlLimit ? parseInt(urlLimit) : REPORT_LIMITS[ReportName.Insights].query;
    // TODO(CC): Remove is_new_count_menu once insights new count menu is rolled out
    // This is a temporary solution to pass is_new_count_menu flag to qapi
    return {
      queryLimits: {limit},
      is_new_count_menu: FeatureRollouts[FeatureRolloutName.InsightsNewCountMenu].isEnabled(currentProject),
    };
  }

  public handleQueryError(err: BaseQueryErrorType): InsightsQueryError {
    return pick(err, [`data`, `status`, `error`, `formula_errors`]) as InsightsQueryError;
  }

  public handleResults({
    params,
    results,
  }: ParamsAndResults<InsightsBookmarkParams, InsightsQueryResults>): ParamsAndResults<
    InsightsBookmarkParams,
    InsightsQueryResults
  > {
    const isCohortQuery = getBehaviorShowClauses(params.sections.show).some(
      (show) => show.behavior.resourceType === InsightsResourceType.Cohorts,
    );
    if (!isCohortQuery) {
      results = addQueryLimitInfo(results);
    }

    return {params, results};
  }
}
